import React, { useState } from "react";
import ChatIcon from "../../assets/icons/chat.svg";
import VideoIcon from "../../assets/icons/video.svg";
import InfoIcon from "../../assets/icons/info.svg";
import StarIcon from "../../assets/icons/star.svg";
import { Pagination } from "./pagination";
import EmptyData from "./NoData";

interface ColumnDefinition<T> {
  key: keyof T;
  label: string;
  width?: string;
  render?: (item: T) => React.ReactNode;
}

interface DataTableProps<T extends object> {
  data: T[];
  columns: ColumnDefinition<T>[];
  itemsPerPage?: number;
  color?: string;
}

const DataTable = <T extends object>({
  data,
  columns,
  itemsPerPage = 10,
  color = "#FEF0EA",
}: DataTableProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Calculate pagination
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = data.slice(startIndex, endIndex);

  // Handle sorting
  const handleSort = (field: keyof T) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <div className="w-full">
      <div className="overflow-x-auto shadow-sm rounded-2xl border border-[#EBEBEB]">
        <table className="w-full text-sm text-left">
          <thead
            className={`text-gray-700 bg-["#FEF0EA"]`}
            style={{
              background: color,
            }}
          >
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key as string}
                  className={`px-6 py-3 cursor-pointer hover:bg-[${color}] ${
                    column.width ?? ""
                  }`}
                  style={{ width: column.width }}
                  onClick={() => handleSort(column.key)}
                >
                  {column.label}{" "}
                  {sortField === column.key &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              currentData.map((item, index) => (
                <tr
                  key={index}
                  className={`bg-white border-b hover:bg-[${color}]`}
                >
                  {columns.map((column) => (
                    <td key={`${index}-${column.key}`} className="px-6 py-4">
                      {column.render ? (
                        React.isValidElement(column.render(item)) ? (
                          column.render(item)
                        ) : (
                          String(column.render(item))
                        )
                      ) : column.key === "activityType" ? ( // Check if the column is activityType
                        item[column.key] === "Chat" ? ( // Render Chat icon
                          <img src={ChatIcon} alt="Chat" className="w-6 h-6" />
                        ) : item[column.key] === "Video Call" ? ( // Render Video icon
                          <img
                            src={VideoIcon}
                            alt="Video Call"
                            className="w-6 h-6"
                          />
                        ) : (
                          <img
                            src={InfoIcon}
                            alt="Video Call"
                            className="w-6 h-6"
                          />
                        )
                      ) : // Fallback to string
                      column.key === "ratings" ? ( // Check if the column is ratings
                        <div className="flex items-center gap-1">
                          <span>{String(item[column.key])}</span>
                          <img src={StarIcon} alt="Star" className="w-6 h-6" />
                        </div>
                      ) : (
                        String(item[column.key])
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr className="bg-white">
                <td></td>
                <td></td>
                <td></td>
                <div className="">
                  <EmptyData />
                </div>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default DataTable;
